// React
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";

// Libraries
import PropTypes from "prop-types";
import { Modal, Icon, Image } from "semantic-ui-react";
import EmojiPicker from "emoji-picker-react";
import Lightbox from "yet-another-react-lightbox";
import { Fullscreen, Zoom, Download, Thumbnails } from "yet-another-react-lightbox/plugins";
import ReactPlayer from "react-player/lazy";
import AudioPlayer from "react-h5-audio-player";
import { filter, map, includes } from "lodash";
import TextAreaAutoSize from "react-textarea-autosize";

// Components
import DownloadCard from "../../Components/DownloadCard";

// Utils
import { getObjectURL } from "@Utils";
import { autoCapitalize } from "@Utils/functionality";

// Styles
import styles from "./styles.module.css";

const PreviewAttachment = ({ attachment, type, text, handleDismiss, handleSend, platformTypes, blackListKeyWords }) => {
	const [caption, setCaption] = useState(text || "");
	const [showEmojis, setShowEmojis] = useState(false);
	const [lightbox, setLightbox] = useState({ open: false, selectedIndex: 0 });
	const [currentIndex, setCurrentIndex] = useState(0);
	const [height, setHeight] = useState(64);
	const [remainingFiles, setRemainingFiles] = useState([...(attachment || [])]);
	const [remainingFileUris, setRemainingFileUris] = useState([]);
	const textAreaRef = useRef(null);
	const highlightsRef = useRef(null);
	const addFileInputRef = useRef(null);

	useLayoutEffect(() => {
		if (textAreaRef.current) {
			const newHeight = textAreaRef.current.offsetHeight + 16;
			if (newHeight >= 210) {
				setHeight(210);
			} else if (newHeight >= 64) {
				setHeight(newHeight);
			} else {
				setHeight(64);
			}
		}
	}, [caption]);

	useEffect(() => {
		(async () => {
			const uris = await Promise.all(
				map(remainingFiles, async (file) => {
					return getObjectURL(file);
				})
			);
			setRemainingFileUris(uris);
		})();

		return () => {
			remainingFileUris.forEach((uri) => {
				return URL.revokeObjectURL(uri);
			});
		};
	}, [remainingFiles]);

	useEffect(() => {
		if (remainingFileUris.length > 0) {
			if (currentIndex >= remainingFileUris.length) {
				setCurrentIndex(remainingFileUris.length - 1);
			}
		}
	}, [remainingFileUris]);

	const handleCancel = () => {
		URL.revokeObjectURL(remainingFileUris[currentIndex]);
		const newFiles = filter(remainingFiles, (_, i) => {
			return i !== currentIndex;
		});
		const newUris = filter(remainingFileUris, (_, i) => {
			return i !== currentIndex;
		});

		if (newFiles.length === 0) {
			handleDismiss();
			return;
		}
		const newIndex = currentIndex >= newFiles.length ? newFiles.length - 1 : currentIndex;
		setRemainingFiles(newFiles);
		setRemainingFileUris(newUris);
		setCurrentIndex(newIndex);
	};

	const toggleEmojiPicker = () => {
		setShowEmojis((prev) => {
			return !prev;
		});
	};

	const handleEmojiInput = ({ emoji }) => {
		setCaption((prev) => {
			return prev + emoji;
		});
		setShowEmojis((prev) => {
			return !prev;
		});
	};

	const handleInput = (e) => {
		let { value } = e.target;
		value = autoCapitalize(value);
		setCaption(value);
	};

	const handleScroll = (e) => {
		if (highlightsRef.current) {
			highlightsRef.current.scrollTop = e.target.scrollTop;
		}
	};

	const renderHighlightedText = () => {
		if (!blackListKeyWords || blackListKeyWords.length === 0) return caption;
		const escapedKeywords = blackListKeyWords.map((k) => {
			return k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		});
		const regex = new RegExp(`\\b(${escapedKeywords.join("|")})\\b`, "gi");
		const parts = caption.split(regex);
		return parts.map((part, i) => {
			if (
				blackListKeyWords.some((k) => {
					return new RegExp(`^${k}$`, "i").test(part);
				})
			) {
				return (
					<span key={i} className={styles.blockedMark}>
						{part}
					</span>
				);
			}
			return <span key={i}>{part}</span>;
		});
	};

	const handleSendMessage = async () => {
		const filesWithCaptions = map(remainingFiles, (file, index) => {
			return {
				file,
				caption: index === remainingFiles.length - 1 ? caption : "",
			};
		});
		const result = await handleSend(filesWithCaptions);
		if (result !== false) {
			handleDismiss();
		}
	};

	const handleOpenLightbox = (index) => {
		setLightbox({ open: true, selectedIndex: index });
	};

	const handleCloseLightbox = () => {
		setLightbox({ open: false, selectedIndex: 0 });
	};

	const renderFile = () => {
		switch (type) {
			case "Image":
				return (
					<div className={styles.imageContainer}>
						<Lightbox
							open={lightbox.open}
							plugins={[Download, Fullscreen, Zoom, Thumbnails]}
							close={handleCloseLightbox}
							index={lightbox.selectedIndex}
							slides={remainingFileUris.map((src) => {
								return {
									src,
									thumbnail: src,
								};
							})}
							zoom={{
								maxZoomPixelRatio: 5,
								zoomInMultiplier: 2,
							}}
						/>
						<Image
							className={styles.image}
							src={remainingFileUris[currentIndex]}
							onClick={() => {
								handleOpenLightbox(currentIndex);
							}}
						/>
					</div>
				);
			case "Video":
				return <ReactPlayer controls url={remainingFileUris[currentIndex]} height="90%" width="100%" />;
			case "Audio":
				return (
					<div className={styles.audio}>
						<AudioPlayer
							src={remainingFileUris[currentIndex]}
							volume={1.0}
							autoPlayAfterSrcChange={false}
							showJumpControls={false}
							layout="horizontal-reverse"
							customAdditionalControls={[]}
							customVolumeControls={[]}
						/>
					</div>
				);
			case "Document":
				return <DownloadCard fileName={attachment[0]?.name} />;
			default:
				return null;
		}
	};

	const handleAddMoreFiles = (e) => {
		const newFiles = [...e.target.files];
		if (newFiles.length > 0) {
			const newUris = newFiles.map((file) => {
				return URL.createObjectURL(file);
			});
			setRemainingFiles((prev) => {
				return [...prev, ...newFiles];
			});
			setRemainingFileUris((prev) => {
				return [...prev, ...newUris];
			});
			setCurrentIndex((prev) => {
				return prev + newFiles.length;
			});
		}
		e.target.value = null;
	};

	const handleOpenFileInput = () => {
		if (addFileInputRef.current) {
			addFileInputRef.current.click();
		}
	};

	const handleThumbnailClick = (index) => {
		setCurrentIndex(index);
	};

	const handleThumbnailKeyDown = (e, index) => {
		if (e.key === "Enter") {
			setCurrentIndex(index);
		}
	};

	const handleKeyDown = async (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			await handleSendMessage();
		}
	};

	return (
		<Modal className={styles.modal} open closeIcon closeOnDimmerClick={false} onClose={handleDismiss}>
			<Modal.Content className={styles.content}>
				<div className={styles.fileContainer} style={{ maxHeight: `calc(100% - ${type === "Image" ? "9.25rem" : "3.5rem"})` }}>
					{renderFile()}
				</div>
				<div>
					{type === "Image" && (
						<div className={styles.thumbnailsContainer}>
							{map(remainingFileUris, (thumb, index) => {
								const isActive = index === currentIndex;
								return (
									<div
										key={`thumb-${index}`}
										className={`${styles.thumbnailWrapper} ${isActive ? styles.activeThumbnail : ""}`}
										onClick={() => {
											handleThumbnailClick(index);
										}}
										role="button"
										tabIndex={0}
										onKeyDown={(e) => {
											handleThumbnailKeyDown(e, index);
										}}>
										<img src={thumb} alt={`thumbnail-${index}`} className={styles.thumbnail} />

										{isActive && remainingFileUris.length > 1 && (
											<div className={styles.topRightActions}>
												<div
													className={styles.cancelButton}
													onClick={handleCancel}
													role="button"
													tabIndex={0}
													onKeyDown={(e) => {
														return e.key === "Enter" && handleCancel();
													}}
													aria-label="Cancelar imagen actual">
													<Icon name="close" className={styles.cancelIcon} />
												</div>
											</div>
										)}
									</div>
								);
							})}

							<div
								className={styles.thumbnailWrapper}
								onClick={handleOpenFileInput}
								role="button"
								tabIndex={0}
								onKeyDown={(e) => {
									return e.key === "Enter" && handleOpenFileInput();
								}}
								aria-label="Agregar más imágenes">
								<div className={styles.addMoreBox}>
									<Icon name="plus" size="large" className={styles.addMoreIcon} />
								</div>
							</div>

							<input ref={addFileInputRef} type="file" accept="image/*" multiple hidden onChange={handleAddMoreFiles} />
						</div>
					)}
					<div className={styles.inputContainer} style={{ height }}>
						{showEmojis && (
							<div className={styles.picker}>
								<EmojiPicker
									onEmojiClick={handleEmojiInput}
									autoFocusSearch={false}
									previewConfig={{ showPreview: false }}
									style={{ zIndex: 5 }}
								/>
							</div>
						)}
						<div
							className={showEmojis ? styles.focusedIconContainer : styles.buttonIconContainer}
							role="button"
							tabIndex="-1"
							onClick={toggleEmojiPicker}>
							<Icon name={showEmojis ? "close" : "smile outline"} size="large" />
						</div>

						<div className={styles.input}>
							{includes(platformTypes, "Captions") && (
								<div className={styles.inputWrapper}>
									<div ref={highlightsRef} className={styles.highlights}>
										{renderHighlightedText()}
										{caption.endsWith("\n") && <br />}
									</div>
									<TextAreaAutoSize
										placeholder="Escriba un mensaje"
										value={caption}
										onChange={handleInput}
										onKeyDown={handleKeyDown}
										id="1"
										name="textarea"
										ref={textAreaRef}
										className={styles.textarea}
										minRows={1}
										maxRows={10}
										onScroll={handleScroll}
									/>
								</div>
							)}
						</div>
						<div className={styles.buttonIconContainer} role="button" tabIndex="-1" onClick={handleSendMessage}>
							<Icon name="send" size="large" />
						</div>
					</div>
				</div>
			</Modal.Content>
		</Modal>
	);
};

PreviewAttachment.propTypes = {
	attachment: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
	type: PropTypes.string,
	text: PropTypes.string,
	handleDismiss: PropTypes.func,
	handleSend: PropTypes.func,
	platformTypes: PropTypes.array,
	blackListKeyWords: PropTypes.array,
};

export default PreviewAttachment;
